import {FC, useEffect, useRef, useState} from 'react'
import {useHistory, useParams} from 'react-router-dom'
import {useLayout} from '../../../_metronic/layout/core'
import {defaultProfileImage} from '../../utils/constants'
import {getExpertDetail} from '../../services/expertDirectoryService'
import {Expert, formatRate, ownerTypeLabel} from './expertDirectory'

// WP-X5 — expert detail (Book an Expert). Bio, expertise, listed rate + a "Request engagement" CTA.
// The engagement request/negotiation flow itself is WP-X6; until then the CTA explains it's coming.
// BLOCKED on the X4 expert read API — a failed fetch shows a "not available yet" note.
const ExpertDetailPage: FC = () => {
  const {id} = useParams<{id: string}>()
  const history = useHistory()
  const {config, setLayout} = useLayout()
  const restoreRef = useRef(config)

  const [expert, setExpert] = useState<Expert | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(false)

  useEffect(() => {
    restoreRef.current = config
    setLayout({
      ...config,
      header: {...config.header, display: true},
      aside: {...config.aside, display: true},
      toolbar: {...config.toolbar, display: false},
      footer: {...config.footer, display: true},
    })
    return () => setLayout(restoreRef.current)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    let active = true
    const load = async () => {
      try {
        setLoading(true)
        setError(false)
        const data = await getExpertDetail(id)
        if (active) setExpert(data)
      } catch (err) {
        if (active) setError(true)
      } finally {
        if (active) setLoading(false)
      }
    }
    load()
    return () => {
      active = false
    }
  }, [id])

  const handleRequestEngagement = () => {
    history.push(`/book-expert/${id}/request`)
  }

  if (loading) {
    return <div className='text-center py-10'>Loading…</div>
  }

  if (error || !expert) {
    return (
      <div className='text-center py-10 text-muted'>
        This expert isn’t available yet. The expert directory is blocked on the expertise X4 backend
        deploy.
      </div>
    )
  }

  return (
    <div className='expert-detail-page'>
      <button
        type='button'
        className='nb-link-underline-500 mb-6'
        onClick={() => history.push('/book-expert')}
      >
        ← Back to experts
      </button>

      <div className='card p-8'>
        <div className='d-flex flex-wrap align-items-center gap-6 mb-6'>
          <img
            src={defaultProfileImage}
            alt=''
            width={96}
            height={96}
            className='rounded-circle'
            style={{objectFit: 'cover'}}
          />
          <div>
            <h1 className='mb-1'>{expert.displayName || 'Expert'}</h1>
            {ownerTypeLabel(expert.ownerType) && (
              <span className='badge badge-light-secondary'>
                {ownerTypeLabel(expert.ownerType)}
              </span>
            )}
            {expert.headline && <div className='text-gray-700 mt-2'>{expert.headline}</div>}
          </div>
          <div className='ms-auto text-end'>
            <div className='text-muted fs-8 text-uppercase'>Listed rate</div>
            <div className='fs-3 fw-bold'>
              {formatRate(expert.listedRate, expert.currency, expert.rateType)}
            </div>
            {expert.availabilityNote && (
              <div className='text-muted fs-8 mt-1'>{expert.availabilityNote}</div>
            )}
          </div>
        </div>

        {(expert.expertiseAreas ?? []).length > 0 && (
          <div className='mb-6'>
            <div className='text-muted fs-8 text-uppercase mb-2'>Expertise</div>
            <div className='d-flex flex-wrap gap-2'>
              {(expert.expertiseAreas ?? []).map((a) => (
                <span key={a.id} className='badge badge-light-primary'>
                  {a.name}
                </span>
              ))}
            </div>
          </div>
        )}

        {expert.bio && (
          <div className='mb-8'>
            <div className='text-muted fs-8 text-uppercase mb-2'>About</div>
            <div className='text-gray-800' style={{whiteSpace: 'pre-line'}}>
              {expert.bio}
            </div>
          </div>
        )}

        <div className='d-flex'>
          <button type='button' className='btn nb-btn-primary' onClick={handleRequestEngagement}>
            Request engagement
          </button>
        </div>
      </div>
    </div>
  )
}

export default ExpertDetailPage
